import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { SiteBriefChoiceType, SiteBriefFontCatalogEntryType } from '../api.types';
import { playCreativeBoom } from '../utils/creativeBoom';
import { resolveChrome } from '../utils/siteBriefChoiceChrome';
import { paletteFromSwatches } from '../utils/siteBriefPalette';
import { formatSiteBriefChoiceAnswer, type SiteBriefChoiceDraft } from '../utils/siteBriefSurvey';
import SiteBriefDesignDirectionPreview from './SiteBriefDesignDirectionPreview';
import SiteBriefFontConstructor from './SiteBriefFontConstructor';
import SiteBriefFontSpecimen from './SiteBriefFontSpecimen';
import SiteBriefPaletteConstructor from './SiteBriefPaletteConstructor';
import SiteBriefQuestionPanel, { QuestionIcon } from './SiteBriefQuestionPanel';

type SiteBriefChoiceCardProps = {
  choice: SiteBriefChoiceType;
  disabled: boolean;
  onSubmit?: (answer: string) => void;
  draft?: SiteBriefChoiceDraft;
  onDraftChange?: (draft: SiteBriefChoiceDraft) => void;
  // Inside a question group, which draws the panel and the footer itself.
  embedded?: boolean;
  // Marks a question of a group that the person skipped.
  skipped?: boolean;
  // Enter in the own-answer field: a single card sends, a group moves on.
  onEnter?: () => void;
  customAnswerMaxLength?: number;
  // Used for the card's own labels when the card does not carry the language Evo identified.
  fallbackLanguage?: string;
  // Fetches the font catalog for the font constructor of a typography card.
  loadFontCatalog?: () => Promise<SiteBriefFontCatalogEntryType[]>;
};

type FontPairType = { heading: string; body: string };

const DEFAULT_FONT_PAIR: FontPairType = { heading: 'Playfair Display', body: 'Lora' };

const CUSTOM_ANSWER_MAX_LENGTH = 500;
const CREATIVITY_LEVELS = 3;
// Temporarily hidden; keep the controls and saved card data available for restoring them later.
const SHOW_PALETTE_CONSTRUCTOR = false;
const SHOW_TYPOGRAPHY_CARDS = false;

// The tradeoff note of an option opens from a small badge, on hover, keyboard focus or a tap.
function CaveatFlag({ id, text, label }: { id: string; text: string; label: string }) {
  const [open, setOpen] = useState(false);

  return (
    <button
      type="button"
      className={`sb-choice-card__flag ${open ? 'is-open' : ''}`}
      aria-label={label}
      aria-expanded={open}
      onClick={() => setOpen((current) => !current)}
      onBlur={() => setOpen(false)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setOpen(false);
        }
      }}
    >
      <QuestionIcon name="info" size={14} />
      <span className="sb-choice-card__tip" role="tooltip" id={id}>
        {text}
      </span>
    </button>
  );
}

function Swatches({ colors }: { colors: string[] }) {
  return (
    <span className="sb-choice-card__swatches" aria-hidden="true">
      {colors.map((color, index) => (
        <span key={`${color}-${index}`} className="sb-choice-card__swatch" style={{ backgroundColor: color }} />
      ))}
    </span>
  );
}

export default function SiteBriefChoiceCard({
  choice,
  disabled,
  onSubmit,
  fallbackLanguage,
  loadFontCatalog,
  draft,
  onDraftChange,
  embedded = false,
  skipped = false,
  onEnter,
  customAnswerMaxLength = CUSTOM_ANSWER_MAX_LENGTH,
}: SiteBriefChoiceCardProps) {
  const multiple = choice.multi_select === true;
  // The creativity dial needs its three levels; anything else falls back to an ordinary card.
  const dial = choice.kind === 'creativity_level' && choice.options.length === CREATIVITY_LEVELS && !multiple;
  const [localDraft, setLocalDraft] = useState<SiteBriefChoiceDraft>({
    selected: [],
    customText: '',
    customSelected: false,
  });
  const currentDraft = draft ?? localDraft;
  const { selected, customText, customSelected } = currentDraft;
  const updateDraft = (changes: Partial<SiteBriefChoiceDraft>) => {
    const next = { ...currentDraft, ...changes };
    if (onDraftChange) {
      onDraftChange(next);
    } else {
      setLocalDraft(next);
    }
  };
  const customInput = useRef<HTMLInputElement>(null);
  const stopBoom = useRef<(() => void) | null>(null);
  // A constructor, when enabled, is shown as one more option.
  const palette = choice.kind === 'palette' && !multiple;
  const typography = choice.kind === 'typography' && !multiple;
  const hasConstructor = (palette && SHOW_PALETTE_CONSTRUCTOR) || (typography && SHOW_TYPOGRAPHY_CARDS);
  const ownIndex = choice.options.length;
  const [paletteDraft, setPaletteDraft] = useState<string[]>(() => paletteFromSwatches(choice.options[0]?.swatches));
  const [fontDraft, setFontDraft] = useState<FontPairType>(() => choice.options[0]?.fonts ?? DEFAULT_FONT_PAIR);
  const [fontCatalog, setFontCatalog] = useState<SiteBriefFontCatalogEntryType[] | null>(null);
  const [fontCatalogFailed, setFontCatalogFailed] = useState(false);
  const customAnswer = customText.trim();
  const hasChoice = selected.length > 0 || (customSelected && customAnswer.length > 0);
  const labelId = useId();
  const inputName = `${labelId}-answer`;
  const chrome = resolveChrome(choice.language, fallbackLanguage);
  // Cards saved before skipping existed have no skip answer, so they show no skip button.
  const skipAnswer = choice.skip_answer?.trim();
  const layout = dial ? 'visual' : palette ? 'palette' : typography ? 'fonts' : 'list';

  // The effect belongs to this card only; it is cut short when the card goes away.
  useEffect(
    () => () => {
      stopBoom.current?.();
    },
    [],
  );

  const ownSelected = hasConstructor && selected.includes(ownIndex);

  // The catalog is only needed once the person opens the font constructor.
  useEffect(() => {
    if (!typography || !ownSelected || fontCatalog || !loadFontCatalog) {
      return;
    }
    let active = true;
    loadFontCatalog()
      .then((fonts) => {
        if (active) {
          setFontCatalog(fonts);
        }
      })
      .catch(() => {
        if (active) {
          setFontCatalogFailed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [typography, ownSelected, fontCatalog, loadFontCatalog]);

  const toggle = (index: number, input: HTMLElement) => {
    // Only choosing the boldest level, the last one on the dial, plays the effect; the other cards stay plain.
    if (dial && index === CREATIVITY_LEVELS - 1 && !selected.includes(index)) {
      stopBoom.current?.();
      stopBoom.current = playCreativeBoom((input.closest('.sb-choice-card__option') ?? input).getBoundingClientRect());
    }
    if (multiple) {
      updateDraft({
        selected: selected.includes(index) ? selected.filter((item) => item !== index) : [...selected, index],
      });
      return;
    }
    // Preserve the text buffer, but only the selected answer is submitted.
    updateDraft({ selected: [index], customSelected: false });
    // The constructor starts from the scheme or pair the person looked at last.
    const picked = choice.options[index];
    if (palette && picked) {
      setPaletteDraft(paletteFromSwatches(picked.swatches));
    }
    if (typography && picked?.fonts) {
      setFontDraft(picked.fonts);
    }
  };

  const selectOwnAnswer = () => {
    updateDraft(multiple ? { customSelected: true } : { selected: [], customSelected: true });
  };

  const submit = () => {
    if (disabled || !hasChoice || !onSubmit) {
      return;
    }
    if (!multiple && customSelected && customAnswer) {
      onSubmit(customAnswer);
      return;
    }
    if (ownSelected) {
      onSubmit(palette ? chrome.paletteAnswer(paletteDraft) : chrome.fontsAnswer(fontDraft.heading, fontDraft.body));
      return;
    }
    onSubmit(formatSiteBriefChoiceAnswer(choice, { selected, customText, customSelected }));
  };

  if (choice.kind === 'typography' && !SHOW_TYPOGRAPHY_CARDS) {
    return null;
  }

  const card = (
    <div className="sb-choice-card">
      <div className="sb-choice-card__header">
        <h3 id={labelId} className="sb-choice-card__question">
          {choice.question}
        </h3>
        <p className="sb-choice-card__hint">
          {multiple ? chrome.chooseAny : chrome.chooseOne}
          {skipped && <span className="sb-choice-card__chip">{chrome.skipped}</span>}
        </p>
      </div>
      <div className="sb-choice-card__body">
        <div
          className={`sb-choice-card__options sb-choice-card__options--${layout}`}
          role={multiple ? 'group' : 'radiogroup'}
          aria-labelledby={labelId}
          style={
            layout === 'palette'
              ? ({ '--sb-choice-columns': Math.max(choice.options.length, 1) } as CSSProperties)
              : undefined
          }
        >
          {choice.options.map((option, index) => {
            const isSelected = selected.includes(index);
            const caveatId = option.caveat ? `${labelId}-caveat-${index}` : undefined;
            const swatchesOnly = palette && option.swatches.length > 0;
            return (
              <div
                key={`${option.title}-${index}`}
                className={`sb-choice-card__option ${isSelected ? 'is-selected' : ''} ${caveatId ? 'has-flag' : ''}`}
              >
                <label className="sb-choice-card__label">
                  {dial && (
                    <span className="sb-choice-card__visual">
                      <SiteBriefDesignDirectionPreview optionIndex={index} />
                    </span>
                  )}
                  {typography && option.fonts && (
                    <span className="sb-choice-card__visual sb-choice-card__visual--fonts">
                      <SiteBriefFontSpecimen chrome={chrome} heading={option.fonts.heading} body={option.fonts.body} />
                    </span>
                  )}
                  <span className="sb-choice-card__row">
                    <input
                      type={multiple ? 'checkbox' : 'radio'}
                      name={inputName}
                      checked={isSelected}
                      disabled={disabled}
                      // A palette shows only its colours; its name and mood stay available to assistive technology.
                      aria-label={swatchesOnly ? [option.title, option.description].filter(Boolean).join('. ') : undefined}
                      aria-describedby={caveatId}
                      onChange={(event) => toggle(index, event.currentTarget)}
                    />
                    {option.swatches.length > 0 && <Swatches colors={option.swatches} />}
                    {!swatchesOnly && (
                      <span className="sb-choice-card__copy">
                        <span className="sb-choice-card__title">{option.title}</span>
                        {option.description && (
                          <span className="sb-choice-card__description">{option.description}</span>
                        )}
                      </span>
                    )}
                  </span>
                </label>
                {option.caveat && caveatId && <CaveatFlag id={caveatId} text={option.caveat} label={chrome.caveatInfo} />}
              </div>
            );
          })}
        </div>
        {hasConstructor && (
          <div className={`sb-choice-card__option ${ownSelected ? 'is-selected' : ''}`}>
            <label className="sb-choice-card__label">
              <span className="sb-choice-card__row">
                <input
                  type="radio"
                  name={inputName}
                  checked={ownSelected}
                  disabled={disabled}
                  onChange={(event) => toggle(ownIndex, event.currentTarget)}
                />
                {palette && <Swatches colors={paletteDraft} />}
                <span className="sb-choice-card__copy">
                  <span className="sb-choice-card__title">{palette ? chrome.ownPalette : chrome.ownFonts}</span>
                  <span className="sb-choice-card__description">
                    {palette ? chrome.ownPaletteHint : chrome.ownFontsHint}
                  </span>
                </span>
              </span>
            </label>
          </div>
        )}
        {ownSelected && palette && (
          <SiteBriefPaletteConstructor chrome={chrome} colors={paletteDraft} disabled={disabled} onChange={setPaletteDraft} />
        )}
        {ownSelected && typography && (
          <SiteBriefFontConstructor
            chrome={chrome}
            catalog={fontCatalog}
            failed={fontCatalogFailed}
            heading={fontDraft.heading}
            body={fontDraft.body}
            disabled={disabled}
            onChange={(heading, body) => setFontDraft({ heading, body })}
          />
        )}
        {/* The creativity dial offers its three levels only. */}
        {!dial && (
          <div className={`sb-choice-card__option sb-choice-card__option--own ${customSelected ? 'is-selected' : ''}`}>
            <input
              type={multiple ? 'checkbox' : 'radio'}
              name={inputName}
              checked={customSelected}
              disabled={disabled}
              aria-label={chrome.customOption}
              onChange={() => {
                if (multiple && customSelected) {
                  updateDraft({ customSelected: false });
                  return;
                }
                selectOwnAnswer();
                customInput.current?.focus();
              }}
            />
            <input
              ref={customInput}
              type="text"
              className="sb-choice-card__own-input"
              value={customText}
              disabled={disabled}
              maxLength={customAnswerMaxLength}
              placeholder={chrome.customPlaceholder}
              aria-label={chrome.customLabel}
              // Typing or clicking selects the row; moving through it with Tab does not.
              onClick={() => {
                if (!customSelected) {
                  selectOwnAnswer();
                }
              }}
              onChange={(event) => {
                updateDraft(
                  multiple
                    ? { customText: event.target.value, customSelected: true }
                    : { customText: event.target.value, selected: [], customSelected: true },
                );
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  if (onEnter) {
                    onEnter();
                  } else {
                    submit();
                  }
                }
              }}
            />
          </div>
        )}
      </div>
      {!embedded && (
        <div className="sb-choice-card__footer">
          {skipAnswer && (
            <button
              type="button"
              className="sb-question-button sb-question-button--ghost"
              disabled={disabled}
              onClick={() => onSubmit?.(skipAnswer)}
            >
              {chrome.skip}
            </button>
          )}
          <button
            type="button"
            className="sb-question-button sb-question-button--primary"
            disabled={disabled || !hasChoice}
            onClick={submit}
          >
            {chrome.send}
            <QuestionIcon name="arrow-right" />
          </button>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return card;
  }
  return (
    <SiteBriefQuestionPanel chrome={chrome} summary={choice.question} lang={choice.language}>
      {card}
    </SiteBriefQuestionPanel>
  );
}
